/**
 * NumeriUtili — Iter122ju
 * Tab "Numeri utili" nella Bacheca. Due viste:
 * - Studio (admin): gestione categorie + assegnazione fornitori per ogni condominio (Iter122jw).
 * - Condòmino: lista categorie con card fornitori (nome, telefono, email)
 */
import { useEffect, useState } from "react";
import { Phone, Mail, Plus, Trash2, Building2, Send } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { CategoryIcon } from "./CategoryIcon";
import { NumeriUtiliAssegna } from "./NumeriUtiliAssegna";

// Iter122jz · normalizza il numero per il tel: URI (mantiene un eventuale + iniziale)
function telHref(tel) {
  const raw = (tel || "").trim();
  if (!raw) return "";
  const plus = raw.startsWith("+") ? "+" : "";
  return `tel:${plus}${raw.replace(/[^\d]/g, "")}`;
}


export function NumeriUtiliCondomino() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/condomino/numeri-utili").then((r) => setList(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center py-8 text-slate-500" data-testid="numeri-utili-loading">Caricamento…</div>;
  if (!list.length) return <div className="text-center py-8 text-slate-500" data-testid="numeri-utili-empty">Nessun numero utile ancora disponibile.</div>;
  return (
    <div className="space-y-4" data-testid="numeri-utili-condomino">
      {list.map((c) => {
        return (
          <Card key={c.id} className="p-4 border-2 border-brand/10" data-testid={`nu-cat-${c.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <CategoryIcon categoria={c.nome} />
              <h3 className="font-bold text-lg text-slate-800">{c.nome}</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {c.fornitori.map((f) => (
                <div key={f.id} className="p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`nu-forn-${f.id}`}>
                  <div className="font-bold text-slate-800 mb-2">{f.nome}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {f.telefono && (
                      <a href={telHref(f.telefono)}
                        data-testid={`nu-forn-tel-${f.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:scale-95 transition"
                        aria-label={`Chiama ${f.nome} al ${f.telefono}`}>
                        <Phone className="w-3.5 h-3.5" /> {f.telefono} · Chiama
                      </a>
                    )}
                    {f.email && (
                      <a href={`mailto:${f.email}`}
                        data-testid={`nu-forn-mail-${f.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand text-white text-xs font-bold hover:bg-brand-dark active:scale-95 transition max-w-full"
                        aria-label={`Scrivi a ${f.nome}`}>
                        <Mail className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{f.email} · Scrivi</span>
                      </a>
                    )}
                    {f.pec && f.pec !== f.email && (
                      <a href={`mailto:${f.pec}`}
                        data-testid={`nu-forn-pec-${f.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border-2 border-brand text-brand text-xs font-bold hover:bg-brand/5 active:scale-95 transition max-w-full"
                        aria-label={`Scrivi PEC a ${f.nome}`}>
                        <Send className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">PEC</span>
                      </a>
                    )}
                    {!f.telefono && !f.email && !f.pec && (
                      <span className="text-xs italic text-slate-400">Nessun contatto disponibile</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export function NumeriUtiliStudio() {
  const [cats, setCats] = useState([]);
  const [nuovoNome, setNuovoNome] = useState("");
  const [busy, setBusy] = useState(false);
  const [condomini, setCondomini] = useState([]);
  const [selCid, setSelCid] = useState("");
  const [showGestCat, setShowGestCat] = useState(false);

  const load = () => api.get("/numeri-utili/categorie").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/condomini").then((r) => setCondomini(r.data || [])).catch(() => {});
  }, []);
  const aggiungi = async () => {
    const nome = nuovoNome.trim();
    if (!nome) return;
    setBusy(true);
    try {
      await api.post("/numeri-utili/categorie", { nome, ordine: (cats.length + 1) * 10 });
      toast.success(`Categoria "${nome}" aggiunta`);
      setNuovoNome("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile creare la categoria");
    } finally { setBusy(false); }
  };
  const elimina = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.nome}"? Verranno rimosse anche le assegnazioni per tutti i condomini.`)) return;
    try {
      await api.delete(`/numeri-utili/categorie/${c.id}`);
      toast.success("Categoria eliminata");
      load();
    } catch { toast.error("Eliminazione non riuscita"); }
  };
  return (
    <div className="space-y-4" data-testid="numeri-utili-studio">
      {/* Iter122jw · Selezione condominio → assegnazione fornitori per categoria */}
      <Card className="p-4 border-2 border-brand/10">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="p-2 rounded-lg bg-brand/10"><Building2 className="w-5 h-5 text-brand" /></div>
          <h3 className="font-bold text-lg text-slate-800">Assegnazione fornitori per condominio</h3>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Scegli il condominio e, per ogni categoria, seleziona i fornitori da mostrare ai condòmini nella tab "Numeri utili" della Bacheca.
        </p>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Condominio</label>
        <select data-testid="nu-cond-select" value={selCid}
          onChange={(e) => setSelCid(e.target.value)}
          className="h-11 w-full px-3 border-2 border-slate-300 rounded-lg bg-white text-base font-semibold">
          <option value="">— Seleziona un condominio —</option>
          {condomini.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
        {selCid && (
          <div className="mt-4">
            <NumeriUtiliAssegna condominioId={selCid} />
          </div>
        )}
      </Card>

      {/* Gestione categorie (collassabile) */}
      <Card className="p-4 border-2 border-slate-200">
        <button type="button" data-testid="nu-toggle-gest-cat"
          onClick={() => setShowGestCat((v) => !v)}
          className="w-full flex items-center justify-between text-left">
          <div>
            <div className="font-bold text-slate-800">Gestione categorie</div>
            <div className="text-xs text-slate-500">Aggiungi o rimuovi le categorie comuni a tutti i condomìni ({cats.length}).</div>
          </div>
          <span className="text-brand text-sm font-semibold">{showGestCat ? "Nascondi" : "Mostra"}</span>
        </button>
        {showGestCat && (
          <div className="mt-3">
            <div className="space-y-2 mb-3">
              {cats.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-200" data-testid={`nu-cat-row-${c.id}`}>
                  <span className="flex-1 font-semibold text-slate-800">{c.nome}</span>
                  <Button size="sm" variant="outline" onClick={() => elimina(c)} className="h-8 border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`nu-cat-delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="nu-new-cat-input" placeholder="Nuova categoria (es. Ascensori)"
                value={nuovoNome} onChange={(e) => setNuovoNome(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && aggiungi()} className="flex-1" />
              <Button data-testid="nu-new-cat-btn" onClick={aggiungi} disabled={busy || !nuovoNome.trim()} className="bg-brand hover:bg-brand-dark">
                <Plus className="w-4 h-4 mr-1" /> Aggiungi
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
